import { useCallback, useEffect, useMemo, useState } from 'react';
import { AgendaApi } from 'services';
import { BookingAgenda, WeekAgenda } from './components';
import { BookingAgendaProvider } from './components/BookingAgenda/contexts/BookingAgendaContext';
import { Box, CircularProgress, Grid, Typography } from '@material-ui/core';
import { createAgendaBody } from 'utils';
import { toastMessages } from 'config/constants';
import { useCompany } from 'contexts/CompanyContext';
import { useEmployee } from 'contexts/EmployeeContext';
import { useHistory, useLocation } from 'react-router-dom';
import { useTheme } from '@material-ui/core/styles';
import { WeekAgendaProvider } from './components/WeekAgenda/contexts/WeekAgendaContext';
import AgendaTransferModal from 'components/AgendaTransferModal';
import BlockWithoutStepModal, { ENDING_LOCATIONS } from 'components/BlockWithoutStepModal';
import CustomSelect from 'components/CustomSelect';
import DefaultButton from 'components/DefaultButton';
import DefaultIconButton from 'components/DefaultIconButton';
import routes from 'config/routes';
import toast from 'react-hot-toast';
import useStyles from './styles';
import configDairy from '_assets/dairy/calendar.svg';
import hoveredConfigDairy from '_assets/dairy/calendarHovered.svg';
import exportDairy from '_assets/dairy/transfer.svg';
import hoveredExportDairy from '_assets/dairy/transferHovered.svg';

const Agenda = ({ config, onStepChange = () => {}, register }) => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const theme = useTheme();

  const { currentCompany, companySteps, companyStepLoading } = useCompany();
  const { employees } = useEmployee();

  const [page, setPage] = useState(config ? 2 : 1);

  const filteredEmployees = useMemo(() => employees.filter((e) => e.is_enabled), [employees]);
  const employee = location?.state?.employee
    ? filteredEmployees.find((e) => e.id === location?.state?.employee.id)
    : filteredEmployees[0];

  const [agendaLoading, setAgendaLoading] = useState(true);
  const [isPageBlocked, setPageBlocked] = useState(false);

  const [monitor, setMonitor] = useState(employee ?? null);
  const [transferState, setTransferState] = useState({
    enabled: false,
    openModal: false,
    saving: false,
  });

  useEffect(() => {
    setPageBlocked(
      !companySteps.employee_created ||
        !companySteps.place_created ||
        !companySteps.service_created ||
        (!register ? !companySteps.agenda_created : false),
    );
  }, [companySteps, register]);

  const handleBlockedPageClose = () => {
    history.push(routes.home.root);
  };

  const setTransferStateByName = (name, value) => {
    setTransferState((prev) => ({ ...prev, [name]: value }));
  };

  const handleEmployeeClick = (monitor) => {
    setMonitor(monitor);
    setAgendaLoading(true);
    setTransferStateByName('enabled', false);
  };

  const handleAgendaLoaded = useCallback((events) => {
    setTransferStateByName('enabled', events.length > 0);
  }, []);

  const transferAgenda = async (agendas) => {
    setTransferStateByName('saving', true);
    try {
      await AgendaApi.transferAgenda({ agendas });
      setTransferStateByName('saving', false);
      // Trigger Agenda update
      setMonitor(null);
      setTimeout(() => setMonitor(filteredEmployees[0]));
    } catch (_) {
      setTransferStateByName('saving', false);
      toast.error(toastMessages.error.TRANSFER_AGENDA);
    }
  };

  const handleConfirmTransfer = (events) => {
    const agendas = createAgendaBody(events);
    setTransferStateByName('openModal', false);
    setTimeout(() => transferAgenda(agendas));
  };

  return (
    <>
      <Grid className={classes.root} container alignItems="center" justifyContent="center" spacing={3}>
        <Grid container item xs={12} alignItems="center" justifyContent="center">
          {!register && (
            <Grid container item xs={12} justifyContent="center">
              <Typography variant="h1">Agenda</Typography>
            </Grid>
          )}

          <Grid
            className={classes.header}
            container
            item
            xs={12}
            alignItems="center"
            justifyContent="center"
            style={{ marginTop: !register ? theme.spacing(3) : 0 }}
          >
            <Grid container item xs={8} justifyContent="center">
              <CustomSelect
                disabled={agendaLoading}
                itemList={filteredEmployees.map((m) => ({ displayName: `${m.first_name} ${m.last_name}`, ...m }))}
                labelField="displayName"
                idField="id"
                selectedItem={monitor}
                onClick={handleEmployeeClick}
                justifyContent="center"
              />
            </Grid>

            {!register && (
              <Grid
                className={classes.buttonContainer}
                container
                item
                xs={4}
                justifyContent="flex-end"
                style={{ paddingBottom: filteredEmployees.length === 0 ? theme.spacing(4) : 0 }}
              >
                {page === 1 && (
                  <Box className={classes.buttonBox}>
                    <DefaultIconButton
                      icon={configDairy}
                      hoveredIcon={hoveredConfigDairy}
                      iconAlt="Icône de configuration de l'agenda"
                      iconText="Configurer"
                      onClick={() => {
                        history.push(routes.register.agenda.root, {
                          employee: monitor,
                          origin: history.location.pathname,
                        });
                      }}
                    />
                    <DefaultIconButton
                      icon={transferState['saving'] ? <CircularProgress size={20} /> : exportDairy}
                      hoveredIcon={hoveredExportDairy}
                      iconAlt="Icône de configuration de l'agenda"
                      iconText="Transférer"
                      disabled={transferState['saving'] || !transferState['enabled']}
                      onClick={() => setTransferStateByName('openModal', true)}
                    />
                  </Box>
                )}
                {page === 2 && (
                  <DefaultButton color="primary" variant="outlined" onClick={() => setPage(1)}>
                    Retour à l'agenda
                  </DefaultButton>
                )}
              </Grid>
            )}
          </Grid>
        </Grid>

        <Grid container item xs={12}>
          {page === 1 && (
            <BookingAgendaProvider monitorId={monitor?.id} onLoaded={(loading) => setAgendaLoading(loading)}>
              <BookingAgenda
                onLoaded={handleAgendaLoaded}
                stepsLoading={companyStepLoading}
                transferOpen={transferState['openModal']}
              />
            </BookingAgendaProvider>
          )}
          {page === 2 && (
            <WeekAgendaProvider
              company={currentCompany}
              monitor={monitor}
              onLoaded={(loading) => setAgendaLoading(loading)}
            >
              <WeekAgenda monitor={monitor} onStepChange={onStepChange} stepsLoading={companyStepLoading} />
            </WeekAgendaProvider>
          )}
        </Grid>
      </Grid>

      <BlockWithoutStepModal
        endingLocation={ENDING_LOCATIONS.agenda}
        open={isPageBlocked}
        onClose={handleBlockedPageClose}
      />

      <AgendaTransferModal
        employee={monitor}
        open={transferState['openModal']}
        title="Transfert Agenda"
        onClose={() => setTransferStateByName('openModal', false)}
        onConfirm={handleConfirmTransfer}
      />
    </>
  );
};

export default Agenda;
